{
	"title": "Courses",
	"subtitle": "Courses, topics and what to study next",
	"eyebrow": "Course",
	"list": {
		"title": "Courses",
		"empty": "No courses yet",
		"emptyHint": "A course turns your materials into a program: topics in a working order, each with a note, flashcards and a quiz.",
		"emptyCta": "Create a course",
		"create": "Create course",
		"createTitle": "New course",
		"createSubtitle": "Name it and add materials — we'll build the program.",
		"createNameLabel": "Course title",
		"createNamePlaceholder": "For example, Calculus, term 2",
		"createExamLabel": "Exam date (optional)",
		"createGoalLabel": "Goal (optional)",
		"createGoalPlaceholder": "For example: score 80+ on the final exam",
		"createGoalHint": "What you are preparing for. Shapes the program and what the tutor suggests.",
		"createSubmit": "Create",
		"createCancel": "Cancel",
		"createSuccess": "Course created",
		"createError": "Could not create the course",
		"defaultTitle": "My course",
		"search": "Search a course",
		"noMatches": "No courses match the filter",
		"segments": {
			"active": "Active",
			"completed": "Completed",
			"archived": "Archive",
			"all": "All"
		},
		"calendar": "Study calendar",
		"templates": "Program templates",
		"courseTemplates": "Course catalog",
		"myTemplates": "My templates"
	},
	"create": {
		"path": {
			"title": "How should we build the course?",
			"subtitle": "Three ways in. Only the last one needs your files.",
			"empty": {
				"title": "Empty",
				"desc": "A space for your own materials: files, lectures, web sources and a tutor who reads them"
			},
			"topic": {
				"title": "By topic",
				"desc": "Name the topic — we'll draft the program, check your level and tune it to you"
			},
			"catalog": {
				"title": "From the catalog",
				"desc": "Ready-made programs: exam prep, university courses, languages"
			}
		},
		"empty": {
			"defaultTitle": "New course",
			"creating": "Creating the course — one moment"
		},
		"finder": {
			"title": "What do you want to learn?",
			"subtitle": "We'll look for a ready course first. Nothing fits — we build your own on this topic.",
			"placeholder": "For example: exam physics",
			"idleTitle": "Name the topic",
			"idleHint": "Start typing — we'll show matching courses from the catalog.",
			"offlineTitle": "The catalog needs a connection",
			"offlineHint": "Catalog search runs on the server. Check the connection and try again.",
			"errorTitle": "Could not search the catalog",
			"errorHint": "The catalog did not answer. Nothing is lost — try again.",
			"retry": "Try again",
			"emptyTitle": "Nothing like that in the catalog yet",
			"emptyHint": "Build your own course on “{query}”? We assemble the program from the topic — no materials needed.",
			"buildOwn": "Build my own course",
			"ownCourse": "I'll build my own on this topic",
			"all": "Browse the whole catalog",
			"starting": "Starting the course — one moment"
		},
		"brief": {
			"title": "Course by topic",
			"subtitle": "Four short questions and the draft program is ready.",
			"steps": {
				"topic": "Topic",
				"goal": "Goal",
				"level": "Level",
				"materials": "Materials",
				"review": "Review"
			},
			"back": "Back",
			"next": "Next",
			"submit": "Build the draft program",
			"topic": {
				"question": "What do you want to learn?",
				"hint": "This becomes the course name — you can rename it later.",
				"placeholder": "For example: physics, grade 11",
				"footnote": "No materials needed: we build the program from the topic.",
				"matchTitle": "Similar courses are already in the library",
				"matchHint": "You can open a ready-made program and start it — what you typed here is saved.",
				"matchAll": "Show all"
			},
			"goal": {
				"question": "What is it for?",
				"hint": "The goal shapes the program and what the tutor suggests.",
				"placeholder": "For example: pass the exam with 90+",
				"skip": "Skip — I'll add the goal later"
			},
			"level": {
				"question": "Where do we start?",
				"hint": "Your level sets how deep the first topics go.",
				"options": {
					"beginner": "Beginner",
					"intermediate": "Intermediate",
					"advanced": "Advanced"
				},
				"unknown": {
					"title": "Not sure — check with a test",
					"desc": "We'll draft an intermediate program and offer a short test that tunes the topics to you"
				},
				"descriptions": {
					"beginner": "New to the topic — start from the basics",
					"intermediate": "I know the basics, need depth and practice",
					"advanced": "Confident with the basics — go for hard cases"
				},
				"audience": {
					"question": "Who is this for?",
					"hint": "This sets the language of the course — vocabulary, examples and tone. Depth stays the same.",
					"adult": "An adult",
					"options": {
						"3-7": "A child aged 3–7",
						"7-11": "A child aged 7–11",
						"11-14": "A teen aged 11–14",
						"14-17": "A teen aged 14–17"
					}
				}
			},
			"materials": {
				"question": "Got your own notes or textbooks?",
				"hint": "The program will use them. Files go to your library — the course picks them up.",
				"library": "From the library",
				"libraryHide": "Hide the library",
				"selected": "Selected: {count, plural, one {# material} other {# materials}}",
				"none": "I have no materials — continue"
			},
			"review": {
				"question": "All correct?",
				"hint": "Check your answers and we'll build the program.",
				"topic": "Topic",
				"goal": "Goal",
				"level": "Level",
				"materials": "Materials",
				"notSet": "Not set",
				"noMaterials": "No materials",
				"cost": {
					"time": "about a minute",
					"generations": "1 generation from your daily limit"
				},
				"failed": "Couldn't build the draft program",
				"failedGeneric": "The build didn't finish. No course was created — please try again.",
				"retry": "Try again"
			}
		}
	},
	"card": {
		"sourcesCount": "{count, plural, one {# source} other {# sources}}",
		"topicsCount": "{count, plural, one {# topic} other {# topics}}",
		"progress": "{done} / {total}",
		"progressAria": "{done} of {total} topics completed",
		"currentTopicEyebrow": "Current topic",
		"currentTopicLabel": "Topic {index} · {title}",
		"minutesShort": "~{count} min",
		"continue": "Continue",
		"open": "Open course",
		"emptyTitle": "No materials yet",
		"emptyHint": "Add materials — we'll build the program.",
		"emptyCta": "Add materials",
		"building": "Building the program…",
		"completed": "Course completed",
		"staleBadge": "Composition changed",
		"actions": "Course actions",
		"mastery": "Mastery",
		"masteryValue": "{pct}%"
	},
	"detail": {
		"back": "To courses",
		"meta": "{sources} · {topics}",
		"examDate": "until {date}",
		"noExamDate": "No exam date",
		"emptyProgram": "Add materials to build the program",
		"emptyProgramHint": "We need a few sources — the program is built from their content.",
		"addSources": "Add materials",
		"buildProgram": "Build the program",
		"buildProgramStarted": "Building the program — it takes up to a minute",
		"buildProgramError": "Could not start building the program",
		"building": "Building the program",
		"buildingHint": "AI is reading the sources and laying out the topics. The page will refresh itself.",
		"sectionProgress": "{done}/{total}",
		"nowStudying": "Now studying",
		"openTopicPage": "Open full topic page",
		"tabs": {
			"program": "Program",
			"composition": "Composition",
			"about": "About"
		},
		"stale": {
			"title": "Composition changed",
			"added": "{count, plural, one {+# material} other {+# materials}}",
			"cta": "See what changes"
		},
		"composition": {
			"title": "Composition",
			"hint": "Materials and folders the program is built from",
			"add": "Add",
			"remove": "Remove from course",
			"removed": "Removed from the course",
			"removeError": "Could not remove from the course",
			"added": "{count, plural, one {# material added} other {# materials added}}",
			"addError": "Could not add to the course",
			"empty": "Nothing here yet",
			"folderItems": "{count, plural, one {# material} other {# materials}}",
			"openFolder": "Open folder",
			"addTitle": "Add sources",
			"addDescription": "Pick materials or whole folders — a folder stays a live link.",
			"tabMaterials": "Materials",
			"tabFolders": "Folders"
		},
		"courseWide": {
			"title": "Build across the course",
			"hint": "Final quiz, single note and flashcards — from every source at once",
			"finalQuiz": "Final quiz",
			"singleNote": "Single note",
			"allFlashcards": "All flashcards",
			"glossary": "Course glossary",
			"generate": "Create",
			"open": "Open",
			"warnMany": "{count} sources in the course — this is a costly generation and takes a few minutes."
		},
		"topicStatus": {
			"done": "Completed",
			"current": "Current",
			"pending": "Not started",
			"generating": "Preparing"
		},
		"tooManyMaterials": "There are a lot of sources — the program may come out coarse. Consider splitting the course.",
		"belowThreshold": "Not enough material yet: {materials} of {materialsRequired} sources and {words} of {wordsRequired} words.",
		"buildingSteps": {
			"read": "Reading the sources",
			"structure": "Laying out the topics",
			"schedule": "Spreading the workload"
		},
		"actions": {
			"rename": "Rename",
			"goalParams": "Goal and parameters",
			"renameTitle": "Rename the course",
			"renameSubmit": "Save",
			"renamed": "Course renamed",
			"renameError": "Could not rename the course",
			"pause": "Pause",
			"paused": "Course paused",
			"pauseError": "Could not pause the course",
			"resume": "Resume",
			"resumed": "Course resumed",
			"resumeError": "Could not resume the course",
			"archive": "Archive",
			"archived": "Course archived",
			"archiveError": "Could not archive the course",
			"restore": "Restore from archive",
			"restored": "Course restored",
			"restoreError": "Could not restore the course",
			"delete": "Delete course",
			"deleteConfirmTitle": "Delete «{title}»?",
			"deleteConfirmDescription": "The program, topics and progress will be gone. The materials stay in the library.",
			"deleted": "Course deleted",
			"deleteError": "Could not delete the course"
		}
	},
	"topic": {
		"back": "To the course",
		"position": "Topic {index} of {total}",
		"minutesShort": "~{count} min",
		"steps": {
			"step1": {
				"eyebrow": "Understand",
				"label": "Understand",
				"finishCta": "I've got it — open step 2",
				"finishHint": "Read or listen first. Or mark done when you're ready — step 2 unlocks."
			},
			"step2": {
				"eyebrow": "Check",
				"label": "Check",
				"locked": "Opens after step 1",
				"unlocked": "Step 2 is open — check yourself"
			},
			"step3": {
				"eyebrow": "Close the gaps",
				"label": "Close the gaps",
				"locked": "Opens after steps 1 and 2"
			}
		},
		"route": {
			"aria": "Step {step} of 3: {label}",
			"stepAria": "Step {step}: {label}",
			"potentialHint": "Appears if the quiz finds gaps"
		},
		"channels": {
			"alsoAvailable": "also available",
			"note": {
				"title": "Read the note",
				"meta": "{minutes} min · {terms, plural, one {# term} other {# terms}}",
				"cta": "Read",
				"pending": "The note is being prepared",
				"edit": "Edit"
			},
			"audio": {
				"title": "Listen",
				"idle": "pick a format",
				"cta": "Pick",
				"generating": "Making the audio",
				"generatingEta": "≈ {seconds} sec",
				"ready": "{format} · {duration}",
				"play": "Listen",
				"offline": "offline",
				"offlineAria": "Available offline",
				"failed": "Could not create the audio",
				"retry": "Retry"
			},
			"flashcards": {
				"title": "Flashcards",
				"meta": "{count, plural, one {# card} other {# cards}}",
				"cta": "Study"
			},
			"quiz": {
				"title": "Quiz",
				"meta": "{count, plural, one {# question} other {# questions}}",
				"cta": "Take"
			},
			"voice": {
				"title": "Talk it through",
				"meta": "~{minutes} min",
				"cta": "Start"
			},
			"chat": {
				"title": "Ask in chat",
				"cta": "Ask"
			}
		},
		"gaps": {
			"title": "{topic} — {errors, plural, one {# error} other {# errors}} out of {total}",
			"hint": "We'll go through exactly this, not the whole topic.",
			"preparing": "Preparing the session",
			"slow": "This is taking longer than usual — tap again to retry",
			"retry": "Tap to retry",
			"error": "Could not start the session",
			"chatPrompt": "Help me with the topic \"{topic}\". I struggle with: {gaps}."
		},
		"allMaterials": "All materials of the topic",
		"allMaterialsHint": "Sources, glossary and everything generated for this topic",
		"sources": "Sources",
		"glossary": "Topic glossary",
		"glossaryTerms": "{count, plural, one {# term} other {# terms}}",
		"generateMore": "Generate more",
		"complete": "Complete topic",
		"completeTooltip": "You can complete it at any moment",
		"completed": "Topic completed",
		"completeError": "Could not complete the topic",
		"nextPreparing": "Topic {index} is being prepared",
		"nextReady": "Topic {index} is ready",
		"generating": {
			"title": "Preparing the topic",
			"hint": "It takes under a minute. You can leave — we'll send a notification.",
			"kinds": {
				"note": "Note",
				"glossary": "Glossary",
				"flashcards": "Flashcards",
				"quiz": "Quiz",
				"voicePlan": "Voice session plan"
			},
			"kindReady": "ready",
			"kindPending": "preparing",
			"kindQueued": "queued"
		},
		"prepare": "Prepare the topic",
		"prepareHint": "This topic has no materials yet — we will build the note, flashcards and quiz.",
		"voicePlanSections": "{count, plural, one {# section} other {# sections}}",
		"reader": {
			"back": "Back to the topic route"
		},
		"skipStepError": "Could not skip this step"
	},
	"audioPicker": {
		"title": "How should we voice the topic?",
		"description": "One content, three ways to listen.",
		"formats": {
			"summary": {
				"title": "Summary",
				"description": "Key points only",
				"voices": "1 voice"
			},
			"lecture": {
				"title": "Lecture",
				"description": "Dense, academic",
				"voices": "1 voice"
			},
			"podcast": {
				"title": "Podcast",
				"description": "A dialogue of two",
				"voices": "2 voices"
			}
		},
		"durationMin": "up to ~{minutes} min",
		"submit": "Create",
		"cancel": "Cancel",
		"started": "The audio is being created",
		"error": "Could not create the audio",
		"quotaRecapDaily": "Daily audio limit reached — come back tomorrow",
		"quotaTtsMinutes": "Daily narration minutes limit reached — come back tomorrow",
		"quotaRefunded": "The quota was returned",
		"errorReason": {
			"tier": "Audio recaps are available on Plus and Pro",
			"not-found": "This topic is no longer available — reload the page",
			"no-source": "Narration needs a processed material — pick a ready source or wait for processing to finish",
			"unknown": "Something went wrong. Try again",
			"unavailable": "Audio recaps aren't available right now"
		}
	},
	"diff": {
		"title": "The course will update",
		"added": "Added",
		"removed": "Removed",
		"programChanges": "What changes in the program",
		"newTopics": "{count, plural, one {# new topic at the end} other {# new topics at the end}}",
		"reordered": "topic order {from}–{to}",
		"untouched": "{count, plural, =1 {topic 1 (completed) is not affected} other {topics 1–# (completed) are not affected}}",
		"noChanges": "The program stays as is",
		"dismiss": "Not now",
		"rebuild": "Rebuild the unfinished part",
		"addNew": "Add new topics",
		"applied": "The program is updating",
		"error": "Could not update the program"
	},
	"rebuild": {
		"banner": {
			"title": "{count, plural, one {# material is not in the program yet} other {# materials are not in the program yet}}",
			"titleUnknown": "New materials are not in the program yet",
			"hint": "They already work in the tutor chat and in every new generation. The program itself changes only if you update it.",
			"update": "Update the course",
			"keep": "Keep as is",
			"kept": "Left as is — the program stays unchanged",
			"keepError": "Could not save the choice"
		},
		"dialog": {
			"title": "Update the course",
			"description": "Choose how deep the update goes.",
			"soft": {
				"title": "Update unstarted topics",
				"recommended": "Recommended",
				"hint": "Unstarted topics are built again from the updated sources. Completed and in-progress topics stay untouched, so progress, mastery and cards are kept.",
				"action": "Update unstarted topics"
			},
			"hard": {
				"title": "Rebuild from scratch",
				"hint": "The whole structure is built again from the current sources. Course progress resets to zero: completed topics become unfinished and their artifacts leave the program, staying in your library. This cannot be undone.",
				"action": "Rebuild from scratch",
				"confirm": "Rebuild and reset progress"
			},
			"softStarted": "Updating the program",
			"hardStarted": "Rebuilding the program",
			"error": "Could not update the program"
		}
	},
	"errors": {
		"loadFailed": "Could not load the course",
		"topicLoadFailed": "Could not load the topic",
		"kitFailed": "Could not prepare the topic materials",
		"kitFailedHint": "The quota was returned. Try again.",
		"retry": "Retry",
		"notFound": "Course not found",
		"topicNotFound": "Topic not found",
		"kitQuotaExceeded": "Today's generation limit is used up",
		"kitQuotaExceededHint": "You can create the topic materials tomorrow or on another plan.",
		"kitQuotaExceededHintNoSale": "You can create the topic materials tomorrow."
	},
	"sources": {
		"title": "Sources",
		"drop": {
			"title": "Drop files here",
			"choose": "Choose files",
			"active": "Release to add to the course",
			"hint": "PDF, DOCX, MD, audio, link · added to the course automatically"
		},
		"upload": "Upload",
		"fromLibrary": "From library",
		"record": "Record lecture",
		"filesSection": "Files",
		"foldersSection": "Folders · live link",
		"liveLinkHint": "The folder stays linked: whatever you add to it joins the course too",
		"rowMenu": "Source actions",
		"processing": "Processing",
		"excluding": "Removing from the course",
		"processingCount": "{count, plural, one {# source is still being processed} other {# sources are still being processed}}",
		"openMaterial": "Open material",
		"openFolder": "Open folder",
		"exclude": "Exclude from course",
		"excludeHint": "“{name}” stays in your library — it only leaves this course.",
		"excluded": "Excluded from the course",
		"excludeFailed": "Could not exclude the source",
		"emptyTitle": "No sources yet",
		"emptyHint": "Add files or folders — the course program is built from them.",
		"attachFailed": "Uploaded, but not added to the course",
		"attachFailedHint": "The files are in your library — add them through “From library”.",
		"windowDrop": {
			"title": "Release the files",
			"hint": "They join the course and start processing right away"
		},
		"discover": {
			"title": "Find sources on the web",
			"hint": "We search the open web, read the pages ourselves and show what each one gives you.",
			"queryLabel": "What to look for",
			"queryPlaceholder": "Topic, question or exam name",
			"search": "Search",
			"searching": "Searching…",
			"stop": "Stop",
			"mode": {
				"label": "Search mode",
				"fast": "Fast",
				"deep": "Deep",
				"fastHint": "One pass: 10-30 seconds, top 10 pages to pick from.",
				"deepHint": "Several rounds of searching and reading: 1-3 minutes, ends with a cited report.",
				"deepLocked": "Deep research is available on Plus and Pro. Tap to see the plans.",
				"deepExhausted": "You've used all deep researches this month. Tap to see the plans.",
				"deepRemaining": "{remaining} of {limit} left this month",
				"deepExhaustedNoSale": "You've used all deep researches this month. The limit resets at the start of next month."
			},
			"stage": {
				"planning": "Planning queries",
				"searching": "Searching",
				"ranking": "Judging results",
				"reading": "Reading sources",
				"readingCount": "Reading sources ({done} of {total})",
				"reflecting": "Checking for gaps",
				"synthesizing": "Writing the report",
				"importing": "Adding to the course",
				"round": "Round {round} of {total}"
			},
			"queriesLabel": "How we searched",
			"selectAll": "Select all",
			"clearAll": "Clear selection",
			"import": "Import ({count})",
			"importing": "Importing…",
			"suggestionsLabel": "Our search",
			"openOriginal": "Open original",
			"deleteRun": "Remove these results",
			"runTitle": "Search: {query}",
			"resultsCount": "{count, plural, one {# source} other {# sources}}",
			"type": {
				"official": "Official",
				"encyclopedia": "Encyclopedia",
				"article": "Article",
				"video": "Video",
				"academic": "Academic",
				"practice": "Practice",
				"other": "Other"
			},
			"empty": {
				"title": "Nothing found yet",
				"hint": "Try naming the topic the way it appears in your course."
			},
			"failed": {
				"title": "Search failed",
				"noResults": "Nothing usable came back. Try a different wording.",
				"aiUnavailable": "Search is temporarily unavailable. Try again in a minute.",
				"retry": "Try again"
			},
			"imported": {
				"queued": "{count, plural, one {# source is being imported} other {# sources are being imported}}",
				"duplicate": "{count, plural, one {# page is already in the course} other {# pages are already in the course}}",
				"limitReached": "Your plan's limit of web sources for this course is reached",
				"failed": "Import failed"
			},
			"error": {
				"paywall": "The site is behind a paywall",
				"blocked": "The site refused access — copy the text manually",
				"thin": "Too little readable text — the page may be a script shell or blocked. Paste the text manually or pick another source",
				"robots": "The site forbids automated reading",
				"unreachable": "Could not reach the site",
				"duplicate": "Already in the course",
				"limitReached": "Plan limit reached",
				"noResults": "Nothing found",
				"aiUnavailable": "Search unavailable"
			},
			"row": {
				"refetch": "Refresh from source",
				"openOriginal": "Open original",
				"refetchQueued": "Refreshing the page…",
				"updatedAgo": "updated {days, plural, =0 {today} one {# day ago} other {# days ago}}",
				"neverFetched": "not read yet"
			},
			"importedBadge": "Imported into the course",
			"deepRunningHint": "This takes 1-3 minutes. You can collapse the card — the research keeps running.",
			"importWithReport": "Save report and {count, plural, one {# source} other {# sources}}",
			"report": {
				"readyTitle": "Report saved",
				"readyHint": "{count, plural, =0 {The report was saved} one {The report and # source were saved} other {The report and # sources were saved}}",
				"pendingTitle": "Report is ready",
				"pendingHint": "{count, plural, =0 {Select sources and save to keep the report} one {Select sources and save — the report will be kept with # source} other {Select sources and save — the report will be kept with # sources}}",
				"open": "Open report",
				"sourcesLabel": "{count, plural, one {# report source} other {# report sources}}"
			}
		}
	},
	"workspace": {
		"header": {
			"toggleLeft": "Course panel",
			"calendar": "Course calendar",
			"mastery": "Mastery",
			"masteryValue": "{pct}%",
			"masteryUnavailable": "Mastery is not calculated yet",
			"today": "Today",
			"todayValue": "{done} / {goal} min",
			"noProgram": "The program is not built yet",
			"aboutAria": "About the program"
		},
		"about": {
			"goal": "Goal:",
			"editGoal": "Edit course goal",
			"setGoal": "Set a course goal"
		},
		"mobile": {
			"callBarAria": "Course panels",
			"calendar": "Calendar",
			"exit": "Back to courses",
			"openPlan": "Open course plan",
			"routeAria": "Topic route",
			"placementAria": "Course setup",
			"masteryValue": "{pct}%",
			"sheetHint": "Swipe down to close"
		},
		"panel": {
			"aria": "Course panel",
			"topics": "Plan",
			"topicsAria": "Course plan",
			"sources": "Sources",
			"aboutMore": "more",
			"aboutLess": "less",
			"created": "Artifacts",
			"createdAria": "Course artifacts",
			"createdEmpty": "This course hasn’t created anything yet"
		},
		"journey": {
			"sectionLabel": "Module {index} · {title}",
			"topicDone": "done",
			"topicDoneMastery": "done · mastery {pct}%",
			"topicCurrent": "now · {count} min",
			"topicGenerating": "kit is building · ready before you come back",
			"emptyTitle": "No program yet",
			"emptyHint": "Add materials and build the course — its topics will line up here, in the order you study them.",
			"masteryWeakBadge": "{pct}%",
			"masteryWeakHint": "Completed with gaps · measured {pct}%",
			"masteryStrongBadge": "Known",
			"masteryStrongHint": "The placement test says you already know this topic · measured {pct}%",
			"placementTitle": "Course setup",
			"placementCaption": "Let's check your level",
			"topicLocked": "Finish course setup first"
		},
		"coverage": {
			"partial": "partial coverage",
			"partialHint": "The source is large — generated from part of it.",
			"gapsHint": "These sources were in the generation context but the note never cited them.",
			"showSources": "Show materials",
			"gapsSummary": "{count, plural, one {# source not cited} other {# sources not cited}}"
		},
		"discuss": {
			"topic": {
				"title": "Still have questions on this topic?",
				"cta": "Discuss with the tutor",
				"ctaPrompt": "I still have questions about “{topic}”. Help me work through them",
				"ctaPromptNoTopic": "I still have questions about this topic. Help me work through them"
			},
			"mistakes": {
				"title": "Want to go through what went wrong?",
				"cta": "Discuss with the tutor",
				"ctaPrompt": "I did not pass the checkpoint test on “{topic}”. Help me go through my mistakes",
				"ctaPromptNoTopic": "I did not pass the checkpoint test. Help me go through my mistakes"
			},
			"chips": {
				"simpler": "Explain the main idea of “{topic}” in simpler terms",
				"simplerNoTopic": "Explain the main idea of this topic in simpler terms",
				"examples": "Give me more examples on “{topic}”",
				"examplesNoTopic": "Give me more examples on this topic",
				"mistakes": "Go through my mistakes: {gaps}"
			}
		},
		"right": {
			"tutor": "Tutor"
		},
		"tutor": {
			"contextLabel": "Context",
			"chips": {
				"course": "Course · {count, plural, one {# source} other {# sources}}",
				"topic": "Topic {label} · Step {step}"
			},
			"openSources": "Course sources",
			"openFullChat": "Open in full chat",
			"newChatFailed": "Could not start a new chat",
			"sessionFailed": "Could not open the course chat",
			"sessionErrorTitle": "The course chat did not open",
			"sessionErrorHint": "The history is safe — it stays in the general tutor chat list.",
			"answerFailed": "The answer did not come through",
			"streamBusy": "Wait for the current answer, then send the next one",
			"offlineTitle": "The course chat is offline",
			"offlineHint": "The tutor needs a connection. Earlier answers open as soon as the chat is loaded once.",
			"offlineComposer": "Offline — the tutor answers only online",
			"contextDegraded": "The tutor did not get the current topic — the answer covers the course in general",
			"unvisitedTopic": "This answer leans on topic {label} “{title}”, which you have not covered yet",
			"hints": {
				"explainSimpler": {
					"label": "Explain simpler",
					"prompt": "Explain the topic “{topic}” in simpler words, step by step."
				},
				"keyIdeas": {
					"label": "Key ideas",
					"prompt": "List the key ideas of the topic “{topic}” as a short list."
				},
				"examples": {
					"label": "Give examples",
					"prompt": "Give two or three concrete examples for the topic “{topic}”."
				},
				"checkMe": {
					"label": "Check me",
					"prompt": "Ask me three questions on the topic “{topic}” and check my answers."
				},
				"whereStuck": {
					"label": "Where am I stuck",
					"prompt": "I got the test on “{topic}” wrong. Show what exactly I misunderstood and how to close the gap."
				},
				"courseOverview": {
					"label": "What is this course about",
					"prompt": "Tell me what the course “{course}” covers, based on my sources."
				},
				"whereToStart": {
					"label": "Where to start",
					"prompt": "Suggest where to start studying the course “{course}” and why."
				}
			}
		},
		"placement": {
			"title": "Check your level",
			"hint": "We'll tune the topics to you · about 10 minutes",
			"start": "Take the test",
			"later": "Later",
			"generating": "Building the test…",
			"generatingHint": "Questions are drawn from the course programme — this takes under a minute",
			"openTest": "Open the test",
			"retry": "Try again",
			"failedTitle": "Could not build the placement test",
			"failedHint": "The course programme is untouched. Retry now or take the placement test later.",
			"requestError": "Could not request the placement test",
			"skipError": "Could not postpone the placement test",
			"catalogTitle": "Let's check your level and tune the course to you",
			"catalogHint": "The author's program stays as it is: the test shows what you can move through faster and where to slow down · about 10 minutes",
			"promptTitle": "Level not assessed",
			"outcomeTitle": "Course tuned to your placement test",
			"outcomeStrong": "{count, plural, one {you already know # topic} other {you already know # topics}}",
			"outcomeWeak": "{count, plural, =0 {everything else is still ahead} one {# topic needs attention} other {# topics need attention}}",
			"outcomeStartWeakest": "Start with the weakest topic",
			"outcomeStartFirst": "Go to the first topic",
			"skipNow": "Skip — start now",
			"startCourse": "Start the course",
			"strongHint": "Your placement test says you already know this topic — {pct}%. You can go straight to the check.",
			"strongHintOther": "The assessment says you already know this topic — {pct}%. You can go straight to the check.",
			"strongCta": "Go to the test",
			"menuTake": "Placement test",
			"menuRetake": "Retake the level test",
			"outcomeHint": "The program is tuned to your answers: familiar topics go faster, gaps get more time.",
			"outcomeStrongLabel": "topics you already know",
			"outcomeWeakLabel": "topics need attention",
			"outcomeDraftHint": "We marked what you already know and where to slow down. You can adjust the program and approve it.",
			"backToDraft": "Back to the program"
		},
		"presets": {
			"quick": "5 minutes",
			"quickHint": "Only the cards that are due",
			"commute": "On the go",
			"commuteHint": "Audio channel of the topic",
			"deep": "Dig in",
			"deepHint": "Note plus the tutor alongside"
		},
		"step": {
			"one": "Step 1 of 2 · Understand",
			"twoHub": "Step 2 of 2 · Check yourself",
			"twoCards": "Step 2 of 2 · Flashcards",
			"twoQuiz": "Step 2 of 2 · Quiz",
			"quizHint": "Answer the questions to move on",
			"three": "Step 3 of 3 · Close the gaps",
			"done": "Topic completed",
			"calendar": "Course schedule",
			"tools": "Step tools",
			"voice": "Voice lesson with the tutor",
			"voiceHint": "Speak out loud — end the lesson to move on",
			"character": "Review with a character",
			"characterHint": "Stay in the course — go back to gaps when you are done",
			"placement": "Course setup",
			"placementHint": "Take the test or skip to start the course",
			"checkpointHint": "One final check, then the topic is done",
			"checkpointPreparing": "Preparing the checkpoint test…",
			"checkpointBelow": "Mastery {pct}% — we suggest closing the gaps",
			"nextTopicBuilding": "The next topic is still being prepared — it will appear here"
		},
		"cta": {
			"finishStepOne": "Got it — check yourself",
			"toCheck": "To the check",
			"toQuiz": "To the quiz",
			"toGap": "Close the gaps",
			"completeTopic": "Complete the topic",
			"nextTopic": "Next topic",
			"backToCourse": "Back to courses",
			"backToTopic": "Back to the topic",
			"continue": "Continue",
			"startCheckpoint": "Checkpoint test",
			"skipQuiz": "Skip the quiz",
			"skipCards": "Skip the cards",
			"skipCheckpoint": "Skip the final quiz"
		},
		"footer": {
			"progressAria": "Topic progress"
		},
		"scene": {
			"step1": {
				"heading": "Pick how you want to learn this topic",
				"noteTitle": "Read",
				"noteHint": "A structured summary of the topic sources",
				"audioTitle": "Listen",
				"audioHint": "Podcast, lecture or a short recap",
				"alsoAvailable": "Also available",
				"statusDone": "Done",
				"ctaRead": "Read",
				"ctaListen": "Listen",
				"ctaReread": "Read again",
				"ctaRelisten": "Listen",
				"extrasTitle": "Your generations",
				"extraNoteTitle": "Additional note",
				"extraNoteHint": "Generated for gaps or on request",
				"extraUserNoteHint": "Your own note on this topic"
			},
			"step2": {
				"heading": "Check yourself on this topic",
				"cardsTitle": "Flashcards",
				"cardsHint": "Recall the key ideas",
				"cardsMissing": "There are no flashcards in this topic",
				"quizTitle": "Quiz",
				"quizHint": "A short check with confidence scoring",
				"extrasTitle": "Your generations",
				"extraCardsTitle": "Extra flashcards",
				"extraCardsHint": "Generated for gaps or on request",
				"extraQuizTitle": "Extra quiz",
				"extraQuizHint": "Generated for gaps or on request",
				"statusDone": "Done",
				"ctaStart": "Start",
				"ctaContinue": "Continue",
				"ctaRetry": "Retry",
				"ctaResults": "Results"
			},
			"note": {
				"eyebrow": "Step 1 · Make sense of it",
				"untitled": "Topic notes",
				"minutesShort": "{count} min",
				"channelAria": "Reading or listening",
				"channelRead": "Read",
				"channelListen": "Listen",
				"edit": "Edit",
				"done": "Done",
				"backToTopicNote": "Back to the topic notes",
				"emptyTitle": "No notes for this topic yet",
				"emptyHint": "StudyHub reads the topic sources and writes a structured summary — usually under a minute.",
				"emptyCta": "Write the notes",
				"generatingTitle": "Writing the notes",
				"generatingPercent": "{percent}%",
				"generatingStepRead": "Reading the sources",
				"generatingStepWrite": "Writing the summary",
				"generatingStepPolish": "Adding highlights and checks",
				"failedTitle": "The notes could not be written",
				"failedHint": "Nothing is lost — the sources are untouched. Try again.",
				"retry": "Try again",
				"quotaTitle": "Daily generation limit reached",
				"quotaHint": "The notes for this topic will be available tomorrow, or right away on a higher plan.",
				"quotaCta": "See plans",
				"aiNote": "Generated by StudyHub from the topic sources. Double-check figures and dates.",
				"quotaHintNoSale": "The notes for this topic will be available tomorrow."
			},
			"audio": {
				"untitled": "Topic audio",
				"pickTitle": "How would you like to listen?",
				"pickHint": "The content is the same — only the delivery changes. You can switch format at any time.",
				"formatReady": "Ready",
				"formatReadyWithDuration": "Ready · {duration}",
				"formatPending": "Being made…",
				"formatCreate": "Create · up to ~{minutes} min",
				"changeFormat": "Change format",
				"download": "Download MP3",
				"downloadFailed": "Could not prepare the file for download",
				"share": "Share",
				"askTutor": "Ask the tutor",
				"askPrompt": "Let's talk through this audio recap of the topic.",
				"offlineSave": "Save for offline",
				"offlineReady": "Available offline",
				"offlineSaved": "Saved to the device — it will play without a network",
				"offlineFailed": "Could not save the file to the device",
				"generatingTitle": "Recording the audio",
				"generatingHint": "You can leave this screen — the audio will finish on its own and a notification will arrive.",
				"failedTitle": "The audio could not be produced",
				"failedHint": "Your quota has been returned. Pick a format again — or a shorter one.",
				"retry": "Try again",
				"offlineTranscriptTitle": "The transcript needs a connection",
				"offlineTranscriptCached": "The audio itself is on the device and plays offline; the text of the transcript is not stored locally.",
				"offlineTranscriptNoFile": "Neither the transcript nor the audio file is on this device yet. Save the audio for offline while you are online.",
				"createMore": "Create another",
				"customize": "Configure yourself",
				"customizeTitle": "Audio settings",
				"customizeHint": "Pick format, style, voices and length — then start generation for this topic."
			},
			"common": {
				"retry": "Try again",
				"upgradeCta": "See plans"
			},
			"cards": {
				"generating": "Building the deck",
				"emptyTitle": "No flashcards for this topic yet",
				"emptyDescription": "StudyHub turns the topic sources into a deck — usually under a minute.",
				"emptyCta": "Build the deck",
				"failedTitle": "The deck could not be built",
				"failedDescription": "Nothing is lost — the sources are untouched. Try again.",
				"quotaTitle": "Daily generation limit reached",
				"quotaDescription": "The deck for this topic will be available tomorrow, or right away on a higher plan.",
				"quotaDescriptionNoSale": "The deck for this topic will be available tomorrow."
			},
			"quiz": {
				"generating": "Writing the questions",
				"emptyTitle": "No quiz for this topic yet",
				"emptyDescription": "StudyHub writes the questions from the topic sources — usually under a minute.",
				"emptyCta": "Write the quiz",
				"failedTitle": "The quiz could not be written",
				"failedDescription": "Nothing is lost — the sources are untouched. Try again.",
				"quotaTitle": "Daily generation limit reached",
				"quotaDescription": "The quiz for this topic will be available tomorrow, or right away on a higher plan.",
				"submittedTitle": "Quiz submitted",
				"submittedDescription": "The answers are counted and the topic progress is updated. The full breakdown lives on its own screen.",
				"openResults": "Open the breakdown",
				"retake": "Take it again",
				"checkpointPassed": "Checkpoint test passed — {pct}%. The topic is ready to complete",
				"checkpointFailedTitle": "{pct}% — not enough to confirm the topic",
				"checkpointMisses": "Missed: {gaps}",
				"checkpointCloseGaps": "Close the gaps once more",
				"quotaDescriptionNoSale": "The quiz for this topic will be available tomorrow."
			},
			"gap": {
				"eyebrow": "Step 3 · Close the gaps — added after your quiz",
				"eyebrowDeepen": "Step 3 · Go deeper",
				"heading": "{count, plural, one {One gap — about five minutes to close it} other {# gaps — let's close them one by one}}",
				"headingNone": "No gaps — you can still go deeper",
				"headingLowMastery": "Topic mastery is {pct}% — keep practicing",
				"analyzing": "Going through your answers",
				"noneTitle": "No gaps left",
				"noneDescription": "The quiz did not expose a weak subtopic. Everything looks solid — deepen the topic if you want, or complete it.",
				"lowMasteryTitle": "Topic mastery {pct}%",
				"lowMasteryDescription": "The quiz found no weak spots, but topic mastery is still below 80%. Use the tools below to study a bit more.",
				"lowMasteryHint": "Practice with the tools below, then finish the topic when you feel ready.",
				"title": "{count, plural, one {# gap} other {# gaps}}",
				"hint": "We go through exactly this, not the whole topic again.",
				"errors": "{errors, plural, one {# error} other {# errors}} out of {total} in this subtopic",
				"progress": "Completed {pct}%",
				"mastery": "Topic mastery {pct}%",
				"srsQueued": "{count, plural, one {# card from this topic is} other {# cards from this topic are}} already queued for review",
				"routeToast": "The quiz exposed a gap — your route grew a “Close the gaps” step",
				"recommendedTitle": "Recommended ways to close the gap",
				"recommendedDeepenTitle": "Ways to raise mastery",
				"primaryTitle": "Best ways to close it",
				"moreTitle": "More ways to practice",
				"voiceTitle": "Voice tutor",
				"voiceHint": "A {minutes}-minute session aimed at exactly this gap",
				"chatTitle": "Ask in the chat",
				"chatHint": "The tutor already knows this topic",
				"rereadTitle": "Reread the section",
				"rereadHint": "Open the main note for this topic",
				"noteTitle": "Gap note",
				"noteHint": "A short note focused on what you missed",
				"gapNotePrompt": "Write a concise study note for the topic \"{topic}\" that focuses only on these gaps: {gaps}. Structure it so I can save it as a note artifact.",
				"gapNotePromptDeepen": "Write a concise study note that deepens the topic \"{topic}\". Structure it so I can save it as a note artifact.",
				"chatPromptDeepen": "Help me go deeper on the topic \"{topic}\". What should I focus on next?",
				"moreQuizTitle": "Another quiz",
				"moreQuizHint": "A fresh check on the same material",
				"checkpointTitle": "Verification quiz",
				"checkpointHint": "Strict topic check: 80% to pass",
				"gapCardsTitle": "Gap flashcards",
				"gapCardsHint": "Cards built around what you missed",
				"charactersTitle": "Talk it through with a character",
				"charactersHint": "Role-play the weak spot",
				"audioTitle": "Audio format",
				"audioHint": "Listen to the topic again",
				"mindmapTitle": "Mind map",
				"mindmapHint": "See the topic as a structure",
				"mindmapPrompt": "Draw a {type} for the topic “{topic}”, focusing on these gaps: {gaps}. Put the diagram first, then two sentences of commentary.",
				"infographicTitle": "Infographic",
				"infographicHint": "One image that covers the topic",
				"summaryTitle": "Quick summary",
				"summaryHint": "The shortest summary of the topic"
			},
			"done": {
				"chip": "Topic completed",
				"title": "Topic completed",
				"summary": "Completed — {topicPct}%. Mastery — {coursePct}%.",
				"summaryTopicOnly": "Completed — {topicPct}%.",
				"statTerms": "Concepts learned",
				"statSrs": "Back in review",
				"statTime": "Time on the topic",
				"statTimeValue": "~{count} min",
				"statTimeHint": "The planned time for this topic — StudyHub does not track a stopwatch.",
				"nextTitle": {
					"ready": "Topic {index} is ready",
					"preparing": "Topic {index} is being prepared",
					"not-started": "Up next — topic {index}"
				},
				"lastTitle": "That was the last topic",
				"lastHint": "The course backbone is finished. The materials stay available.",
				"remedialTitle": "Gaps left in “{section}”",
				"remedialHint": "You scored low on: {topics}. Add a reinforcement topic that works through exactly those?",
				"remedialAdd": "Add topic",
				"remedialLater": "Not now",
				"remedialError": "Could not add the reinforcement topic. Try again.",
				"nextHint": {
					"ready": "Built while you worked on the previous one",
					"preparing": "Still building — it will be ready when you get there",
					"not-started": "We will build the materials when you open it — under a minute"
				}
			},
			"calendar": {
				"srsNote": "Spaced repetition is shared across all your courses, so reviews are not laid out here.",
				"srsLink": "Open the study calendar",
				"inactive": {
					"paused": {
						"title": "The course is paused",
						"description": "While a course is paused the planner lays out no classes for it. Resume the course and the schedule comes back — nothing is lost."
					},
					"archived": {
						"title": "The course is archived",
						"description": "Archived courses are kept out of the schedule. Restore the course to get its classes back on the calendar."
					},
					"completed": {
						"title": "The course is finished",
						"description": "There is nothing left to schedule — every topic is done. The materials and reviews stay available."
					}
				}
			},
			"voice": {
				"emptyTitle": "Voice lesson on this topic",
				"emptyDescription": "The tutor asks questions out loud and follows the lesson plan for this topic. Nothing is recorded — only the text transcript is kept.",
				"startTitle": "Start the lesson",
				"startHint": "About {minutes} min, voice, right inside the course"
			},
			"character": {
				"emptyTitle": "Character chat on this topic",
				"emptyDescription": "Pick a character from the tools panel or the gap step — the conversation stays inside the course and uses this topic’s materials."
			},
			"placement": {
				"title": "Course setup",
				"hint": "A short test (~10 min) shows what you already know and where to slow down.",
				"failedTitle": "Could not build the placement test",
				"failedHint": "The course programme is untouched. Retry now or skip and start the course.",
				"retake": "Take the test again"
			}
		},
		"topicBar": {
			"back": "Back",
			"noTopic": "No topic selected",
			"position": "Topic {index} of {total}",
			"views": {
				"step1": "Understand",
				"step2": "Check yourself",
				"noteEditor": "Editing notes",
				"cards": "Flashcards",
				"quiz": "Quiz",
				"gap": "Gaps",
				"done": "Topic complete",
				"calendar": "Schedule",
				"tools": "Tools",
				"builder": "Structure",
				"note": "Read",
				"audio": "Listen",
				"voice": "Voice lesson",
				"character": "Character",
				"placement": "Course setup"
			}
		},
		"skip": {
			"flashcards": {
				"title": "Skip the cards?",
				"description": "The deck closes without a review round, so this topic’s terms will not enter your repetition plan. You will go straight to the quiz; the cards stay available in the topic panel.",
				"confirm": "Skip"
			},
			"quiz": {
				"title": "Skip this quiz?",
				"description": "The “Check yourself” step will close without a score: gaps in this topic stay unknown and mastery stays unconfirmed. You can take the quiz later from the topic panel.",
				"confirm": "Skip"
			},
			"checkpoint": {
				"title": "Skip the final quiz?",
				"description": "The topic will close without a final check, so its mastery stays unconfirmed. You can request the final quiz later from the “Close gaps” step.",
				"confirm": "Skip and finish"
			}
		}
	},
	"tools": {
		"busy": "Working…",
		"quotaCta": "See the plans",
		"sceneTitle": "Topic tools",
		"sceneHint": "Formats that help you work through this topic and check yourself.",
		"channels": {
			"read": "Read",
			"watch": "Watch",
			"listen": "Listen",
			"practice": "Practice",
			"plan": "Plan"
		},
		"items": {
			"audio": {
				"title": "Audio format",
				"hint": "Podcast, lecture or a short recap"
			},
			"glossary": {
				"title": "Topic glossary",
				"hint": "Key terms with definitions"
			},
			"userNote": {
				"title": "Your own note",
				"hint": "An empty note that opens in the editor"
			},
			"tutor": {
				"title": "Ask the tutor",
				"hint": "A chat that already knows this topic"
			},
			"calendar": {
				"title": "Course calendar",
				"hint": "The schedule day by day"
			},
			"moreCards": {
				"title": "More flashcards",
				"hint": "Rebuild the deck with a wider set"
			},
			"moreQuiz": {
				"title": "Another quiz",
				"hint": "Fresh questions on the same topic"
			},
			"voice": {
				"title": "Voice tutor",
				"hint": "Say it out loud — the session opens full screen"
			},
			"gapCards": {
				"title": "Cards for the gap",
				"hint": "Built around what the quiz exposed"
			},
			"reread": {
				"title": "Reread the note",
				"hint": "Back to the text of this topic"
			},
			"mindmap": {
				"title": "Mind map",
				"hint": "A diagram of this topic, straight into the chat"
			},
			"infographic": {
				"title": "Infographic",
				"hint": "One picture that lays the topic out, straight into the chat"
			},
			"summary": {
				"title": "Quick recap",
				"hint": "The short version, straight into the chat"
			},
			"characters": {
				"title": "Work through with a character",
				"hint": "A dialogue over the topic materials — the character answers and cites sources"
			}
		},
		"userNote": {
			"failed": "Could not create the note"
		},
		"voice": {
			"planPreparing": "Preparing the lesson plan — the tile wakes up once it is ready",
			"failed": "Could not start the voice session",
			"alreadyActive": "A voice session is already running",
			"continueActive": "Continue session"
		},
		"artifacts": {
			"title": "Artifacts",
			"hint": "Everything created for this course",
			"topicSection": "This topic",
			"courseSection": "Across the course",
			"menu": "Row actions",
			"busy": "Working…",
			"open": "Open",
			"exclude": "Exclude from the course",
			"excludeHint": "“{title}” leaves the course and stays in your library.",
			"excluded": "Excluded from the course",
			"excludeFailed": "Could not exclude it",
			"regenerate": "Regenerate",
			"retryRow": "Try again",
			"regenerateStarted": "Regenerating — it will refresh on its own",
			"emptyTitle": "Nothing here yet",
			"emptyHint": "Build the topic kit — the note, flashcards and quiz will show up here.",
			"emptyAction": "Build the kit",
			"errorTitle": "Could not read the artifacts",
			"errorHint": "The local copy did not open. Try again.",
			"retry": "Retry",
			"kinds": {
				"note": "Note",
				"flashcards": "Flashcards",
				"quiz": "Quiz",
				"audio-recap": "Audio",
				"glossary": "Glossary",
				"source": "Source",
				"diagram": "Diagram",
				"infographic": "Infographic",
				"saved-answer": "Saved reply"
			},
			"state": {
				"generating": "Generating… {percent}%",
				"ready": "Ready",
				"readyIn": "Ready · {topic}",
				"failed": "Didn’t work out",
				"unconfirmed": "Could not confirm it — refresh the page"
			},
			"meta": {
				"cards": "{count, plural, one {# card} other {# cards}}",
				"questions": "{count, plural, one {# question} other {# questions}}",
				"terms": "{count, plural, one {# term} other {# terms}}",
				"minutes": "{count, plural, one {# min} other {# min}}",
				"text": "Text",
				"own": "Your note",
				"srs": "SRS",
				"dialogue": "Dialogue, {count} hosts",
				"narration": "Narration",
				"savedAnswer": "Saved reply",
				"diagram": "Diagram",
				"infographic": "Infographic"
			},
			"download": "Download",
			"downloadFailed": "Could not download the file",
			"delete": "Delete",
			"deleteHint": "“{title}” moves to the trash.",
			"deleteFailed": "Could not delete it",
			"convertToSource": "Make it a source",
			"dismiss": "Hide"
		},
		"offlineHint": "Needs an internet connection",
		"mindmap": {
			"picker": {
				"title": "Pick a diagram",
				"description": "The tutor builds it from this topic. Previews show what the renderer really draws.",
				"submit": "Build it"
			},
			"types": {
				"mindmap": {
					"title": "Mind map",
					"hint": "Ideas branching out of one concept"
				},
				"flowchart": {
					"title": "Flowchart",
					"hint": "Steps, conditions and branches of a process"
				},
				"sequenceDiagram": {
					"title": "Sequence",
					"hint": "Who sends what to whom, in order"
				},
				"stateDiagram-v2": {
					"title": "States",
					"hint": "States of one object and the moves between them"
				},
				"classDiagram": {
					"title": "Classes",
					"hint": "Entities, their fields and relations"
				},
				"erDiagram": {
					"title": "ER diagram",
					"hint": "Tables and the links between them"
				},
				"timeline": {
					"title": "Timeline",
					"hint": "Events laid out by date"
				},
				"gantt": {
					"title": "Gantt chart",
					"hint": "Stages and deadlines of a project"
				}
			},
			"chatPrompt": "Draw a {type} for the topic “{topic}”. Put the diagram first, then two sentences of commentary.",
			"failed": "The tutor answered without a diagram. Try again or pick another type."
		},
		"infographic": {
			"dialog": {
				"title": "Infographic settings",
				"description": "The tutor works out what to show from this topic, then draws it.",
				"submit": "Generate",
				"busy": "Working…"
			},
			"fields": {
				"language": "Language",
				"orientation": "Orientation",
				"style": "Visual style",
				"detail": "Level of detail",
				"freeText": "What to emphasise",
				"freeTextPlaceholder": "Formulas, dates, a comparison — anything worth putting up front"
			},
			"orientations": {
				"landscape": "Landscape",
				"portrait": "Portrait",
				"square": "Square"
			},
			"details": {
				"brief": "Brief",
				"standard": "Standard",
				"detailed": "Detailed"
			},
			"styles": {
				"auto": {
					"title": "Auto",
					"hint": "The model picks what suits the content"
				},
				"flat-vector": {
					"title": "Flat vector",
					"hint": "Solid fills, rounded shapes, a calm palette"
				},
				"editorial": {
					"title": "Editorial",
					"hint": "Magazine layout, strong type, lots of air"
				},
				"blueprint": {
					"title": "Blueprint",
					"hint": "Deep blue, thin white line art, a grid"
				},
				"notebook": {
					"title": "Notebook",
					"hint": "Ruled paper, marker strokes, hand lettering"
				},
				"isometric": {
					"title": "Isometric",
					"hint": "3D blocks at an angle, soft pastel shading"
				},
				"chalkboard": {
					"title": "Chalkboard",
					"hint": "Dark slate, chalk strokes, a few colour accents"
				},
				"mono-minimal": {
					"title": "Mono minimal",
					"hint": "Black on off-white, hierarchy by weight alone"
				}
			},
			"sources": {
				"count": "{count, plural, one {# source from this topic} other {# sources from this topic}}",
				"empty": "No materials yet — it will be drawn from the topic title"
			},
			"chatPrompt": "Make an infographic for the topic “{topic}”.",
			"failed": "Could not start the infographic"
		},
		"summary": {
			"chatPrompt": "Give me a short recap of the topic “{topic}”: the key points and what is worth remembering."
		},
		"topicTitle": "Topic tools",
		"courseWideCta": "Create for the whole course"
	},
	"duplicate": {
		"action": "Copy course",
		"title": "Copy the course",
		"subtitle": "The copy starts from scratch: progress and mastery never carry over.",
		"nameLabel": "Name of the copy",
		"copyName": "{title} (copy)",
		"sources": "Sources",
		"sourcesHint": "Materials and folders from the course composition",
		"program": "Program",
		"programHint": "Sections and topics, all progress reset",
		"artifacts": "Generated artifacts",
		"artifactsHint": "Notes, flashcards and quizzes get their own copies — reviews and attempts start over",
		"artifactsNeedsProgram": "Available when the program is copied",
		"cancel": "Cancel",
		"submit": "Copy",
		"success": "Course copied",
		"open": "Open",
		"error": "Could not copy the course"
	},
	"newCourse": {
		"empty": {
			"title": "A workspace for your materials",
			"subtitle": "Upload what you study from: notes, PDFs and slides, lecture recordings, links and videos. Ask the tutor about your own sources and search the web for what is missing. Whenever you want, StudyHub lays it out into a program by topic."
		},
		"topicBridge": {
			"label": "Build a program from a topic — no materials needed"
		},
		"upload": {
			"aria": "How to add material",
			"tabs": {
				"file": "File",
				"link": "Link",
				"text": "Text",
				"web": "Web"
			},
			"file": {
				"title": "Drop files here or click to choose",
				"titleMobile": "Choose files to upload",
				"formats": "PDF · Word · Excel · PowerPoint · Text · Markdown · images · audio · video",
				"max": "Up to {limit} files at a time"
			},
			"link": {
				"title": "A link to a page or a video",
				"hint": "Articles, documentation, YouTube and podcasts that have a transcript.",
				"cta": "Add a link"
			},
			"text": {
				"title": "Your own text",
				"hint": "A summary from class, a chapter, anything you typed yourself.",
				"cta": "Add text"
			},
			"fromLibrary": "From library",
			"record": "Record a lecture"
		},
		"tips": {
			"howMany": {
				"title": "How many materials to bring",
				"body": "One file is enough to start. The fuller the set, the more the program leans on your own materials: aim for {materials} sources, or a single big one from {words} words. It is a quality mark, not a gate — a program can also be built from a topic alone, with no files."
			},
			"folder": {
				"title": "A folder is a live link",
				"body": "Attach a folder from your library: add a file to it later and the course notices it and offers to update the program."
			}
		},
		"sources": {
			"title": "Course materials"
		},
		"next": {
			"title": "What is next",
			"hint": "Build the program automatically or lay it out yourself",
			"hintProcessing": "Wait for indexing to finish — it takes a few seconds",
			"hintNoSources": "Add at least one source first",
			"hintFailed": "A file failed to process — replace it or add another source",
			"callout": "You can add materials later too — the course will offer to update only the topics you have not started, without resetting progress.",
			"belowRecommended": "Generation needs {materials} sources or about {words} words in total. With less, it will not start — add more materials anytime.",
			"or": "Or"
		},
		"generate": {
			"title": "Generate the course",
			"body": "StudyHub reads the sources, pulls out the topics and groups them into modules. The kit for the first topic is ready right away.",
			"bullet1": "A program by topic — up to {topics} topics, with time estimates",
			"bullet2": "Note, flashcards and quiz for the first topic",
			"bullet3": "Order and titles stay editable afterwards",
			"cta": "Generate the course",
			"cost": "Takes about a minute · 1 generation from your quota"
		},
		"manual": {
			"title": "Lay it out yourself",
			"body": "You set the modules and topics and attach materials to them. Handy when the program is already known — from a syllabus, for example.",
			"bullet1": "Your own modules, topics and order",
			"bullet2": "Materials are attached to topics by hand",
			"bullet3": "AI can still be called for a single module",
			"cta": "Open the builder",
			"cost": "No generations spent"
		},
		"params": {
			"title": "Generation parameters",
			"subtitle": "Three hints for the generator plus the facts it will build on.",
			"locale": "Program language",
			"sources": "Sources",
			"sourcesValue": "{ready} of {total} ready",
			"maxTopics": "Topics at most",
			"threshold": "Minimum to build",
			"thresholdValue": "{materials} sources or {words} words",
			"level": {
				"label": "Level",
				"hint": "Who the course is for. Sets the depth of explanations and the pace.",
				"beginner": "Beginner",
				"intermediate": "Intermediate",
				"advanced": "Advanced"
			},
			"depth": {
				"label": "Program depth",
				"hint": "Planned for about {weeks} weeks of study — the deeper the program, the more it unpacks each source.",
				"quick": "Quick",
				"standard": "Standard",
				"deep": "Deep"
			},
			"focus": {
				"label": "Course focus",
				"placeholder": "Heavier on problem solving, chapters 3-5 for the midterm…",
				"hint": "Optional. Steers both the structure and which parts of the sources are pulled in."
			},
			"editableLater": "Everything stays editable afterwards: rename topics, reorder them, rebuild the program.",
			"close": "Cancel",
			"goal": {
				"title": "Course goal",
				"subtitle": "What you are preparing for and in which mode. Does not rebuild the program.",
				"label": "Goal",
				"placeholder": "For example: score 80+ on the final exam",
				"hint": "Optional. The course keeps the goal in mind: it reaches the topic kit prompt and the tutor context.",
				"notDeclared": "No goal declared yet — the values below are defaults.",
				"noRebuild": "Saving changes the goal only. The program you already built stays as is.",
				"save": "Save",
				"saved": "Course goal saved",
				"saveError": "Could not save the course goal"
			}
		},
		"generating": {
			"tips": {
				"one": "A quiz right after the explanation beats one at the end of the module — recalling locks knowledge in stronger than rereading.",
				"two": "The kit for the next topic is prepared while you go through the current one, so there is nothing to wait for.",
				"three": "The note and the audio are the same content: it is a choice of channel, not two steps in a row."
			}
		}
	},
	"builder": {
		"title": "Course builder",
		"entry": "Edit structure",
		"hint": "Add modules and topics, then attach sources to each topic. The kit — note, flashcards, quiz — is generated when you reach the topic.",
		"suggestOutline": "Suggest a structure",
		"addModule": "Add module",
		"done": "Done",
		"empty": {
			"title": "No modules yet",
			"description": "Build the programme by hand, or let AI suggest a structure from your sources."
		},
		"module": {
			"newTitle": "Module {index}",
			"placeholder": "Module title",
			"titleAria": "Module title",
			"drag": "Reorder module",
			"moveUp": "Move module up",
			"moveDown": "Move module down",
			"delete": "Delete module",
			"confirmDelete": "Delete “{title}”?",
			"confirmDeleteHint": "{count, plural, =0 {The module is empty.} one {# topic with its progress will be removed.} other {# topics with their progress will be removed.}} Artifacts stay in your library.",
			"suggestTopics": "Module topics",
			"addTopic": "Add topic",
			"noTopics": "No topics in this module yet."
		},
		"topic": {
			"newTitle": "New topic",
			"placeholder": "Topic title — e.g. “Documents: DS-2019 and SEVIS”",
			"titleAria": "Topic title",
			"drag": "Reorder topic",
			"moveUp": "Move topic up",
			"moveDown": "Move topic down",
			"moveToModule": "Move to another module",
			"delete": "Delete topic",
			"confirmDelete": "Delete “{title}”?",
			"confirmDeleteHint": "The topic and its progress will be removed. Notes, flashcards and quizzes stay in your library.",
			"attach": "Attach",
			"detach": "Detach",
			"sourceDeleted": "Source deleted",
			"describe": "Topic description",
			"descriptionHint": "A short description helps pick the right sources for this topic and shapes the materials generated for it.",
			"descriptionPlaceholder": "What this topic covers — in one or two sentences"
		},
		"attach": {
			"title": "Attach to the topic",
			"description": "“{topic}” will be built from the sources you pick here.",
			"tabsAria": "What to attach",
			"tabSources": "Sources",
			"tabArtifacts": "Artifacts",
			"cancel": "Cancel",
			"submit": "{count, plural, =0 {Attach} one {Attach #} other {Attach #}}",
			"already": "attached",
			"done": "{count, plural, =0 {Nothing new to attach} one {# item attached} other {# items attached}}",
			"emptySources": "No sources in this course",
			"emptySourcesHint": "Add files on the “Sources” tab — then you can narrow a topic down to some of them.",
			"emptyArtifacts": "No ready artifacts yet",
			"emptyArtifactsHint": "Notes, flashcards, quizzes and audio you already have will show up here.",
			"kind": {
				"note": "Note",
				"flashcards": "Flashcards",
				"quiz": "Quiz",
				"audio": "Audio"
			}
		},
		"draft": {
			"title": "Suggested structure",
			"description": "Nothing is created yet. Uncheck what you do not need and apply.",
			"descriptionSection": "Topics suggested for “{module}”. Nothing is created yet.",
			"summary": "{count, plural, =0 {Nothing selected} one {# topic will be created} other {# topics will be created}}",
			"applying": "Creating {done} of {total}…",
			"applied": "{count, plural, one {# topic created} other {# topics created}}",
			"applyFailed": "Stopped after {count, plural, =0 {no topics} one {# topic} other {# topics}} — try again",
			"discard": "Discard",
			"apply": "Apply"
		},
		"errors": {
			"failed": "Could not apply the change",
			"noSources": "Add at least one source — the structure is built from them"
		}
	},
	"templates": {
		"publish": {
			"action": "Publish as template",
			"actionVersion": "Publish version {version}",
			"title": "Publish course to the catalog",
			"subtitle": "Three steps: author byline, course page description, catalog fields.",
			"nameLabel": "Template name",
			"categoryLabel": "Category",
			"categoryPlaceholder": "Choose a category",
			"descriptionLabel": "Description",
			"descriptionPlaceholder": "What the course covers and who it is for",
			"descriptionCounter": "{count} / {max}",
			"consentTitle": "Your files stay closed",
			"consentBody": "Your files stay closed, but their content becomes available to readers through notes and tutor answers. Readers never see the file list and cannot download anything.",
			"consentCheckbox": "I understand this and want to publish",
			"cancel": "Cancel",
			"submit": "Publish",
			"success": "Course sent for review",
			"error": "Could not publish the template",
			"unpublishedBadge": "Unpublished changes",
			"versionSuccess": "Version {version} sent for review",
			"versionConfirmTitle": "Publish version {version}?",
			"versionConfirmBody": "New readers will start from this version. Those already studying stay on their version until they choose to update. The new version goes through the policy check again — the course leaves the catalog until it passes.",
			"versionConfirmCta": "Publish",
			"back": "Back",
			"next": "Next",
			"steps": {
				"author": "Author",
				"listing": "Description",
				"publish": "Publishing"
			},
			"author": {
				"legend": "Who is this course published by",
				"options": {
					"user": {
						"title": "Under my name",
						"description": "Your name and avatar appear on the course card. Readers can open your other courses."
					},
					"anonymous": {
						"title": "Anonymously",
						"description": "The course goes out without an author: your name will not appear on the course page, in the catalog or in search results. The course is not signed by StudyHub either — it simply has no author."
					}
				},
				"bioLabel": "A few words about you — optional",
				"bioPlaceholder": "For example: biology teacher, nine years of exam prep",
				"bioHint": "Shown in the «Course author» block. Leave it empty to show just your name.",
				"linkLabel": "A link to you — optional",
				"linkPlaceholder": "Website, channel or profile",
				"linkHint": "One link next to your name on the course page.",
				"publicNotice": "A published course is available to everyone on the internet and appears in search results. You can remove it from the catalog at any time — everyone who already started keeps the course.",
				"reviewNotice": "Before it appears in the catalog, the course goes through an automated policy check: no violence, adult content, weapons or other prohibited topics. It usually takes a couple of minutes — until it is approved, only you can see the course."
			},
			"listing": {
				"generatingTitle": "Writing the course description",
				"generatingHint": "No need to wait here: the description keeps generating even if you move on.",
				"steps": {
					"program": "Reading the course outline",
					"outcomes": "Wording what the student will learn",
					"audience": "Working out the audience and level",
					"faq": "Preparing answers to common questions"
				},
				"readyNotice": "The description is written from the course outline. Check the wording — this is exactly what the course page will show.",
				"readyNoticeBlind": "The description is written from the course outline. Fields you fill in here replace the generated text; empty ones stay as generated.",
				"pendingHint": "We will write the description from the course outline right after publishing. Fill the fields in yourself and we will use your text instead of generating one.",
				"failedHint": "The description could not be written. This does not block publishing: the course goes out without storefront texts and the description can be written later.",
				"regenerate": "Generate again",
				"aiChip": "written from the course",
				"summaryLabel": "Short description",
				"summaryPlaceholder": "What the course is about and what it gives",
				"summaryHint": "Shown in the catalog and in search results. 120–160 characters works best.",
				"outcomesLabel": "What the student will learn",
				"outcomePlaceholder": "For example: solve inheritance problems step by step",
				"outcomeAria": "Outcome {index}",
				"addOutcome": "Add an item",
				"removeOutcome": "Remove item",
				"audienceLabel": "Who it is for",
				"audiencePlaceholder": "Who benefits from this course",
				"prerequisitesLabel": "What to know beforehand",
				"prerequisitesPlaceholder": "What preparation is needed to start",
				"faqLabel": "Common questions",
				"faqQuestionPlaceholder": "Question",
				"faqQuestionAria": "Question {index}",
				"faqAnswerPlaceholder": "Answer",
				"faqAnswerAria": "Answer to question {index}",
				"addFaq": "Add a question",
				"removeFaq": "Remove question"
			},
			"publish": {
				"previewLabel": "This is how the course looks in the catalog",
				"previewUntitled": "Untitled",
				"freeNotice": "The course will be published as free — any user on any plan can take it."
			},
			"unpublishAction": "Unpublish",
			"relistAction": "Return to catalog",
			"unpublishConfirmTitle": "Unpublish this course?",
			"unpublishConfirmBody": "The course disappears from the catalog, so new learners cannot find it. Everyone already taking it continues unaffected.",
			"unpublishConfirmCta": "Unpublish",
			"relistConfirmTitle": "Return the course to the catalog?",
			"relistConfirmBody": "The course appears in the catalog again and new learners can enroll.",
			"relistConfirmCta": "Return to catalog",
			"unpublished": "Course unpublished",
			"relisted": "Course is back in the catalog"
		},
		"categories": {
			"exam": "Exam prep",
			"language": "Languages",
			"science": "Science",
			"humanities": "Humanities",
			"tech": "Tech",
			"business": "Business",
			"medicine": "Medicine",
			"other": "Other"
		},
		"status": {
			"draft": "Draft",
			"published": "In the catalog",
			"unpublished": "Unpublished"
		},
		"review": {
			"pending": "In review",
			"manual": "Human review",
			"rejected": "Check failed",
			"rejectedTitle": "The course did not pass the check",
			"rejectedBody": "The course does not meet the catalog rules, so it was not listed. Fix this and publish a new version.",
			"noReason": "No details were given. Check the course for prohibited topics: violence, adult content, weapons, drugs, other people’s personal data.",
			"appeal": "If you think this is a mistake, contact support and a person will look at the course."
		},
		"sources": {
			"authorMaterials": "{count, plural, one {# author material} other {# author materials}}",
			"hint": "The author's files are closed: no list, no previews, no downloads. Their content reaches you through notes, tests and tutor answers."
		},
		"tutor": {
			"notice": "Answers draw on the course author's materials. The sources themselves are closed.",
			"aiDisclaimer": "AI can be wrong — check anything that matters."
		},
		"update": {
			"pill": "Version {version} available",
			"title": "Version {version} available",
			"subtitle": "You are studying version {current}.",
			"warningTitle": "What updating changes",
			"warningBody": "{count, plural, one {# topic you completed was changed} other {# topics you completed were changed}} — they are rebuilt and progress on them resets. Completed and unchanged topics stay as they are.",
			"warningBodySafe": "Changed topics are rebuilt from scratch. None of the topics you completed were changed, so no progress is lost.",
			"summary": "Added {added} · changed {changed} · removed {removed} · unchanged {unchanged}",
			"kind": {
				"added": "New topic",
				"changed": "Updated",
				"renamed": "Renamed",
				"removed": "Removed",
				"unchanged": "Unchanged"
			},
			"noStructuralChanges": "The structure itself has not changed",
			"apply": "Update",
			"later": "Not now",
			"hide": "Hide until next version",
			"applied": "Course updated to version {version}",
			"error": "Could not update the course",
			"willReset": "will be rebuilt, progress resets",
			"frozen": "already completed — stays as it is",
			"hideError": "Could not hide the notice"
		},
		"fork": {
			"title": "Use as a starting point",
			"subtitle": "You get your own copy: structure and generated materials, progress from scratch.",
			"cta": "Use as a starting point",
			"ctaTitle": "Want your own materials here?",
			"ctaHint": "Make a copy of the course and add your own sources to it.",
			"sources": "Author's sources",
			"sourcesHint": "Never transferred — the author's files stay closed",
			"note": "After copying, add your own sources and rebuild the program.",
			"submit": "Make a copy",
			"success": "Copy created — add your sources",
			"error": "Could not copy the course"
		},
		"card": {
			"topics": "{count, plural, one {# topic} other {# topics}}",
			"version": "v{version}",
			"start": "Start course",
			"enrolled": "Course added to your list",
			"enrollError": "Could not start the course",
			"enrollments": "{count, plural, one {# student} other {# students}}",
			"alreadyEnrolled": "You are already on this course"
		},
		"catalog": {
			"title": "Course catalog",
			"subtitle": "Courses published by other authors — with a program, notes and a tutor",
			"back": "Courses",
			"search": "Search courses",
			"categoryAll": "All categories",
			"localeAll": "All languages",
			"emptyTitle": "No published courses yet",
			"emptyHint": "Publish one of your courses as a template — it will show up here.",
			"emptyCta": "Go to my courses",
			"noMatches": "Nothing matches these filters",
			"noMatchesHint": "Try another category or language.",
			"resetFilters": "Reset filters",
			"errorTitle": "Could not load the catalog",
			"errorHint": "Check your connection and try again.",
			"retry": "Try again",
			"offlineTitle": "The catalog needs a connection",
			"offlineHint": "Published courses are not stored on the device — the list loads when you are back online.",
			"tabsLabel": "Catalog tabs",
			"tabCatalog": "Catalog",
			"tabMine": "My templates"
		},
		"mine": {
			"open": "Open",
			"unpublish": "Unpublish",
			"republish": "Return to catalog",
			"unpublishConfirmTitle": "Unpublish this course?",
			"unpublishConfirmBody": "New readers will not be able to start it. Everyone already studying keeps their course and their version.",
			"unpublishConfirmCta": "Unpublish",
			"unpublishedToast": "Course unpublished — new sign-ups are closed",
			"restoredToast": "Course is back in the catalog",
			"statusError": "Could not change the status",
			"draftHint": "No version published yet. Publish the course from its header — then it appears in the catalog.",
			"versionsCount": "{count, plural, one {# version} other {# versions}}",
			"emptyTitle": "You have not published anything yet",
			"emptyHint": "Publish one of your courses as a template — it shows up here and in the catalog.",
			"errorTitle": "Could not load your templates"
		},
		"detail": {
			"back": "Catalog",
			"noDescription": "The author left no description.",
			"outlineTitle": "Program",
			"outlineEmpty": "The program appears once the author publishes a version.",
			"sourcesTitle": "The author's files stay closed",
			"startHint": "You get your own course built from this version: program, notes, cards and tests.",
			"closedHint": "The author closed new sign-ups. Courses already started keep working.",
			"alreadyEnrolled": "You already have a course from this template.",
			"continue": "Continue the course",
			"authorTitle": "Your template",
			"authorHint": "Unpublishing stops new sign-ups; everyone already studying keeps their course. A new version is published from the course header.",
			"notFoundTitle": "Course not found",
			"notFoundHint": "The author may have removed this template.",
			"errorTitle": "Could not load the course"
		}
	},
	"cover": {
		"entry": "Course cover",
		"title": "Course cover",
		"subtitle": "The cover is shown on the course card, in the catalog and in the course header.",
		"promptLabel": "What to change (optional)",
		"promptPlaceholder": "For example: warmer colours, add a globe",
		"promptHint": "Covers never contain text, people or logos — those requests are ignored so the image works in every language.",
		"regenerate": "Generate a new one",
		"regenerateQueued": "Drawing a new cover — it will appear in a moment",
		"regenerateError": "Could not start the generation",
		"upload": "Upload my own",
		"uploadDone": "Cover updated",
		"uploadError": "Could not upload the cover",
		"uploadBadType": "Only JPEG, PNG or WebP files",
		"uploadTooLarge": "The file is larger than 5 MB",
		"preview": "View cover",
		"previewTitle": "Course cover"
	},
	"catalog": {
		"title": "Course catalog",
		"subtitle": "Ready-made programs and courses from other authors — pick one and start",
		"back": "Courses",
		"scopeLabel": "Catalog sections",
		"scopeCatalog": "Catalog",
		"scopeMine": "My templates",
		"emptyTitle": "The catalog is empty for now",
		"emptyHint": "Ready-made exam programs appear here, along with courses published by other authors.",
		"noMatches": "Nothing matches these filters",
		"noMatchesHint": "Try a different area, type or language.",
		"resetFilters": "Reset filters",
		"errorTitle": "Could not load the catalog",
		"errorHint": "Check your connection and try again.",
		"retry": "Try again",
		"offlineTitle": "The catalog needs a connection",
		"offlineHint": "The catalog is not stored on the device — the list loads when you are back online.",
		"loadMore": "Show more",
		"filters": {
			"subjectArea": "Area",
			"subjectAreaAll": "All areas",
			"purpose": "Type",
			"purposeAll": "Any type",
			"origin": "Source",
			"originAll": "Any source",
			"examSystem": "Exam",
			"examSystemAll": "Any exam",
			"locale": "Language",
			"localeAll": "All languages",
			"sort": "Sorting",
			"search": "Search the catalog",
			"pricing": "Price",
			"pricingAll": "Any price"
		},
		"subjectAreas": {
			"math": "Mathematics",
			"science": "Natural sciences",
			"languages": "Languages",
			"humanities": "Humanities",
			"tech": "IT and programming",
			"business": "Business and finance",
			"medicine": "Medicine",
			"arts": "Art and design",
			"countries": "Countries",
			"other": "Other"
		},
		"purposes": {
			"exam": "Exam prep",
			"general": "General course"
		},
		"pricing": {
			"free": "Free",
			"paid": "Paid"
		},
		"origins": {
			"official": "Official programs",
			"author": "From authors"
		},
		"sorts": {
			"popular": "Popular first",
			"new": "Newest first"
		},
		"card": {
			"official": "Official program",
			"inProgress": "Already started",
			"byAuthor": "by {name}",
			"start": "Start course",
			"continue": "Continue the course",
			"started": "Course added to your list",
			"alreadyStarted": "You are already on this course",
			"startError": "Could not start the course",
			"hours": "≈{count, plural, one {# hour} other {# hours}}",
			"topics": "{count, plural, one {# topic} other {# topics}}",
			"enrollments": "{count, plural, one {# student} other {# students}}",
			"reviewPending": "In review",
			"reviewRejected": "Rejected"
		},
		"levels": {
			"beginner": "Beginner",
			"intermediate": "Intermediate",
			"advanced": "Advanced"
		},
		"publish": {
			"subjectAreaLabel": "Area",
			"subjectAreaPlaceholder": "Pick an area",
			"purposeLabel": "Course type",
			"levelLabel": "Level",
			"levelNone": "Not specified",
			"examSystemLabel": "Exam",
			"examSystemNone": "Not specified",
			"localeLabel": "Course language",
			"translationNotice": "The title and description will be translated into every language of the platform so learners can find the course in their own language. The course content is not translated."
		},
		"seo": {
			"title": "Course catalog",
			"description": "Ready-made courses and exam prep programs: notes, flashcards, quizzes and an AI tutor inside every course.",
			"aboutTitle": "What the StudyHub course catalog is",
			"aboutBody": "The catalog brings together exam prep programs from the platform and ready-made courses published by authors. Every course comes with a program of sections and topics, notes, flashcards and quizzes, and the AI tutor answers from that course’s own materials. Start with any of them — progress and schedule adapt to your workload."
		},
		"public": {
			"startSoon": "Enrollment is coming soon",
			"notFoundTitle": "Course not found",
			"notFoundHint": "The course is unpublished or the link is outdated. Take a look at what is in the catalog.",
			"backToCatalog": "Go to catalog"
		}
	},
	"language": {
		"prompt": "This course is in {courseLanguage}. Translate it into {userLanguage}?",
		"translate": "Translate",
		"keepOriginal": "Keep the original",
		"translating": "Translating the course outline — topic titles will update in a moment",
		"error": "Could not change the course language",
		"originalBadge": "Original: {language}",
		"topicError": "Could not translate this topic — it stays in the original language",
		"topicQuota": "Daily generation limit reached — this topic stays in the original language"
	},
	"audio": {
		"originalLanguage": "Narrated in the original course language ({language}) — audio is not translated with the text.",
		"revoice": "Re-narrate in {language}",
		"revoiceQueued": "Narration started — it will appear when ready",
		"revoiceError": "Could not start the narration"
	},
	"draft": {
		"badge": "Draft program",
		"buildingTitle": "Rebuilding the program…",
		"buildingHint": "This usually takes about a minute. The topics will refresh on their own.",
		"failedTitle": "Could not rebuild the program",
		"failedHint": "The previous topics are intact — retry, or approve the program as it is.",
		"retry": "Try again",
		"rebuildStarted": "Rebuilding the program",
		"rebuildAlreadyRunning": "This program is already being built",
		"rebuildError": "Could not start the rebuild",
		"confirmed": "Program approved — you can start now",
		"confirmError": "Could not approve the program",
		"ready": {
			"title": "The program is ready — let's begin",
			"hint": "The first topic is open. You can start now.",
			"step": "Program approved",
			"continue": "Begin"
		},
		"actions": {
			"label": "Draft program",
			"confirm": "Approve program",
			"edit": "Edit program",
			"rebuild": "Rebuild",
			"rebuilding": "Rebuilding…"
		},
		"adjust": {
			"title": "Adjust the draft to your test results?",
			"hint": "Weak topics get a deeper breakdown, strong ones stay on the express path. Nothing is rewritten — we only suggest what to add.",
			"action": "Suggest an adjustment",
			"later": "Not now",
			"readyTitle": "What we suggest adding",
			"readyHint": "Module order stays as it is — these are additions inside existing modules. Uncheck anything you do not want.",
			"rowModule": "Into “{module}”",
			"rowBecause": "Into “{module}” — because of “{topic}”",
			"summary": "{count, plural, one {# topic} other {# topics}} to add",
			"applying": "Adding: {done} of {total}",
			"apply": "Add to the program",
			"discard": "Do not add",
			"applied": "{count, plural, one {# topic added} other {# topics added}}",
			"applyFailed": "Could not add everything: {count, plural, one {# topic} other {# topics}} made it",
			"emptyTitle": "Nothing to adjust",
			"emptyNoWeak": "The test found no weak topics — the program fits as it is.",
			"emptyNoRoom": "The program is already at its maximum size — there is no room for more topics.",
			"close": "Got it",
			"failedTitle": "Could not build an adjustment",
			"failedHint": "The program is unchanged. Retry, or approve the draft as it is.",
			"retry": "Try again"
		},
		"sources": {
			"title": "{count, plural, one {# material is not in the draft yet} other {# materials are not in the draft yet}}",
			"titleUnknown": "New materials are not in the draft yet",
			"hint": "The draft can be rebuilt from scratch — there is no progress in it yet, so nothing is lost.",
			"update": "Update the draft",
			"updating": "Rebuilding…",
			"keep": "Keep as is",
			"kept": "Left as is — the program stays unchanged",
			"keepError": "Could not save the choice"
		},
		"passport": {
			"eyebrow": "Draft topic",
			"noDescription": "This topic has no description — it stays in the program under this title.",
			"contentLater": "Notes, flashcards and the test for this topic appear after you approve the program — they are built as you go, not all at once.",
			"openPlan": "All topics",
			"noTopicTitle": "No topic selected",
			"noTopicHint": "Open the program and pick a topic to see what it covers."
		}
	},
	"intro": {
		"title": "A course is a program from your materials",
		"body": "The AI splits what you uploaded into topics and orders them. Each topic runs a route: understand → check yourself → close the gaps.",
		"bullets": [
			"The program is built from sources, not written by hand",
			"Lessons lay themselves out on the calendar up to the exam",
			"A ready-made program can be taken from the catalog"
		]
	},
	"catalogIntro": {
		"title": "The catalog is theirs, the course is yours",
		"body": "A program taken from here becomes a separate course of your own — with progress of its own that starts from zero.",
		"bullets": [
			"A platform program is generated; an author's course is copied",
			"A taken course stays put when the author ships a new version",
			"The author's materials do not come along; you add your own"
		]
	}
}
